import { useState } from 'react';
import { useUser } from '@clerk/react';
import { CreditCard, ExternalLink, Trash2 } from 'lucide-react';
import { useDocumentTitle } from '@/shared/hooks';
import { useToast } from '@/shared/contexts/ToastContext';
import { ACCOUNT_ACCENT } from './accountStyles';
import { AddCardModal } from './AddCardModal';
import { usePaymentMethods } from './hooks/usePaymentMethods';
import { useDetachPaymentMethod } from './hooks/useDetachPaymentMethod';
import { useRecentStripePayments } from './hooks/useRecentStripePayments';
import { usePurchaseActivity } from './hooks/usePurchaseActivity';
import { isStripePublishableConfigured } from './stripeBrowser';

function formatCardBrand(brand: string): string {
  if (!brand) return 'Card';
  return brand.slice(0, 1).toUpperCase() + brand.slice(1).toLowerCase();
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function formatPaymentDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

function purchaseSourceLabel(source: string): string {
  if (source === 'stripe_webhook') return 'Auto-purchase';
  if (source === 'autopurchase_clearance') return 'Clearance auto-purchase';
  if (source === 'clearance_checkout') return 'Clearance';
  if (source === 'premium_checkout') return 'Premium';
  return source.replace(/_/g, ' ');
}

const AccountBillingPage = () => {
  useDocumentTitle('Billing - BrokerMax');
  const { notify } = useToast();
  const { user } = useUser();
  const stripeUi = isStripePublishableConfigured();
  const {
    data: paymentMethods = [],
    isLoading,
    isError
  } = usePaymentMethods(stripeUi);
  const {
    data: recentPayments = [],
    isLoading: paymentsLoading,
    isError: paymentsError
  } = useRecentStripePayments(stripeUi);
  const {
    data: purchaseActivity = [],
    isLoading: purchaseLoading,
    isError: purchaseError
  } = usePurchaseActivity();
  const detach = useDetachPaymentMethod();
  const [addCardOpen, setAddCardOpen] = useState(false);

  const name =
    user?.firstName ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress?.split('@')[0] ||
    'Agent';

  const profile = {
    email: user?.primaryEmailAddress?.emailAddress,
    name:
      user?.fullName ||
      [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
      undefined
  };

  return (
    <div className="relative z-10">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
        Billing
      </h1>
      <p className="mt-1 max-w-xl text-sm text-slate-700">
        Payment methods and receipts for {name}. Cards are stored by Stripe and
        used for wallet top-ups on the dashboard.
      </p>

      {!stripeUi ? (
        <div
          className={`mt-8 rounded-xl p-8 ${ACCOUNT_ACCENT.cardSurface} text-center text-sm text-slate-600`}
        >
          Set{' '}
          <code className="rounded bg-slate-100 px-1">
            VITE_STRIPE_PUBLISHABLE_KEY
          </code>{' '}
          and{' '}
          <code className="rounded bg-slate-100 px-1">STRIPE_SECRET_KEY</code>{' '}
          to manage saved cards here.
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
              Payment methods
            </h2>
            <button
              type="button"
              onClick={() => setAddCardOpen(true)}
              className="rounded-lg bg-[#2b6cff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb]"
            >
              Add card
            </button>
          </div>

          {isError ? (
            <p className="text-sm text-amber-700">
              Could not load cards. Ensure the API has{' '}
              <code className="rounded bg-slate-100 px-1">
                STRIPE_SECRET_KEY
              </code>{' '}
              and restart{' '}
              <code className="rounded bg-slate-100 px-1">dev:api</code>.
            </p>
          ) : null}

          <div
            className={`rounded-xl ${ACCOUNT_ACCENT.cardSurface} divide-y divide-slate-100`}
          >
            {isLoading ? (
              <p className="p-6 text-sm text-slate-600">
                Loading payment methods…
              </p>
            ) : paymentMethods.length === 0 ? (
              <p className="p-6 text-sm text-slate-600">
                No saved cards yet. Add a card to top up your wallet from the
                dashboard.
              </p>
            ) : (
              paymentMethods.map(pm => (
                <div
                  key={pm.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <CreditCard className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {formatCardBrand(pm.brand)} ···· {pm.last4}
                        {pm.isDefault ? (
                          <span className="ml-2 text-xs font-normal text-slate-500">
                            Default
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-slate-500">
                        Expires {String(pm.expMonth).padStart(2, '0')}/
                        {String(pm.expYear).slice(-2)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={detach.isPending}
                    onClick={() => {
                      detach.mutate(pm.id, {
                        onSuccess: () => notify('success', 'Card removed.'),
                        onError: err =>
                          notify(
                            'warning',
                            err instanceof Error
                              ? err.message
                              : 'Could not remove card'
                          )
                      });
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="mt-10">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
              Purchase activity
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Lead purchases charged to your wallet (including clearance).
            </p>

            {purchaseError ? (
              <p className="mt-3 text-sm text-amber-700">
                Could not load purchase activity.
              </p>
            ) : null}

            <div
              className={`mt-4 overflow-hidden rounded-xl ${ACCOUNT_ACCENT.cardSurface}`}
            >
              {purchaseLoading ? (
                <p className="p-6 text-sm text-slate-600">
                  Loading purchase activity…
                </p>
              ) : purchaseActivity.length === 0 ? (
                <p className="p-6 text-sm text-slate-600">
                  No lead purchases yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[620px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Lead</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Source</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {purchaseActivity.map(p => (
                        <tr key={p.assignmentId} className="text-slate-800">
                          <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-600">
                            {formatPaymentDate(p.purchasedAt)}
                          </td>
                          <td className="px-4 py-3">
                            {p.leadName?.trim() || p.leadId}
                          </td>
                          <td className="px-4 py-3">
                            {p.leadType === 'buyer'
                              ? 'Buyer Lead'
                              : 'Tenant Lead'}
                          </td>
                          <td className="px-4 py-3">
                            {purchaseSourceLabel(p.source)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums">
                            {formatMoney(p.amountCad, 'CAD')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="mt-10">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
              Recent payments (Stripe)
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Card charges on your BrokerMax customer — wallet top-ups appear
              here.
            </p>

            {paymentsError ? (
              <p className="mt-3 text-sm text-amber-700">
                Could not load payment history.
              </p>
            ) : null}

            <div
              className={`mt-4 overflow-hidden rounded-xl ${ACCOUNT_ACCENT.cardSurface}`}
            >
              {paymentsLoading ? (
                <p className="p-6 text-sm text-slate-600">
                  Loading payment history…
                </p>
              ) : recentPayments.length === 0 ? (
                <p className="p-6 text-sm text-slate-600">
                  No card payments yet. Top up your wallet from the dashboard.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Description</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Receipt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recentPayments.map(p => (
                        <tr key={p.id} className="text-slate-800">
                          <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-600">
                            {formatPaymentDate(p.createdAt)}
                          </td>
                          <td className="px-4 py-3">{p.description}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums">
                            {formatMoney(p.amount, p.currency)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                p.status === 'succeeded'
                                  ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-600/15'
                                  : 'bg-slate-100 text-slate-700 ring-1 ring-slate-500/10'
                              }`}
                            >
                              {statusLabel(p.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {p.receiptUrl ? (
                              <a
                                href={p.receiptUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 font-medium text-[#2b6cff] hover:underline"
                              >
                                View
                                <ExternalLink
                                  className="h-3.5 w-3.5 opacity-80"
                                  strokeWidth={2}
                                />
                              </a>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {stripeUi ? (
        <AddCardModal
          open={addCardOpen}
          onOpenChange={setAddCardOpen}
          profile={profile}
          onCardSaved={() =>
            notify(
              'success',
              'Card saved — it appears below and on the dashboard.'
            )
          }
        />
      ) : null}
    </div>
  );
};

export default AccountBillingPage;
